import { Link } from "react-router-dom";
import { ExternalLink, FileText, Unlink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { appPath } from "@/constants/paths";
import { useSetPageDepartment } from "@/features/content/hooks/useContentMutations";
import { AssignPagesDialog } from "@/features/departments/components/AssignPagesDialog";
import {
  DEPARTMENT_PAGES_CONTENT_TYPE,
  useDepartmentPages,
} from "@/features/departments/hooks/useDepartmentPages";
import { useDepartmentPermissions } from "@/hooks/useDepartmentPermissions";

const REVIEW_STATUS_LABEL: Record<string, string> = {
  DRAFT: "مسودة",
  SUBMITTED: "قيد المراجعة",
  APPROVED: "موافَق عليه",
  REJECTED: "مرفوض",
};

interface DepartmentOwnedPagesPanelProps {
  departmentId: string;
}

export function DepartmentOwnedPagesPanel({
  departmentId,
}: DepartmentOwnedPagesPanelProps) {
  const { canManage } = useDepartmentPermissions();
  const { data, isLoading } = useDepartmentPages({ departmentId, pageSize: 10 });
  const unassign = useSetPageDepartment();

  const pages = data?.items ?? [];
  const total = data?.pagination.total ?? pages.length;
  const contentPageUrl = `${appPath("pages")}?department=${departmentId}`;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          {canManage && <AssignPagesDialog departmentId={departmentId} />}
          <div className="text-right">
            <h3 className="font-semibold">ملكية المحتوى</h3>
            <p className="text-xs text-muted-foreground mt-1">
              الصفحات المرتبطة بهذه الدائرة
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : pages.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="لا توجد صفحات مرتبطة"
            description="لم يتم ربط أي صفحات بهذه الدائرة بعد."
          />
        ) : (
          <ul className="space-y-2">
            {pages.map((page) => {
              const id = String(page.id);
              return (
                <li
                  key={id}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2"
                >
                  {canManage && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={unassign.isPending}
                      aria-label="إلغاء الربط"
                      onClick={() =>
                        unassign.mutate({
                          contentType: DEPARTMENT_PAGES_CONTENT_TYPE,
                          id,
                          departmentId: null,
                        })
                      }
                    >
                      <Unlink className="size-4" />
                    </Button>
                  )}
                  <div className="flex flex-wrap items-center gap-1 shrink-0">
                    {page.reviewStatus && (
                      <Badge variant="outline" className="text-xs">
                        {REVIEW_STATUS_LABEL[page.reviewStatus] ??
                          page.reviewStatus}
                      </Badge>
                    )}
                    <Badge
                      variant={page.published ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {page.published ? "منشور" : "مسودة"}
                    </Badge>
                  </div>
                  <div className="flex-1 text-right min-w-0">
                    <div className="font-medium text-sm truncate">
                      {page.title ?? page.slug ?? id}
                    </div>
                    {page.slug && (
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {page.slug}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {total > 0 && (
          <div className="flex justify-end border-t pt-3">
            <Button type="button" variant="link" size="sm" asChild>
              <Link to={contentPageUrl}>
                عرض الكل في الصفحات ({total})
                <ExternalLink className="size-3.5 mr-1" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
